import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export function useClearChat(onSuccess?: () => void) {
  const [isClearing, setIsClearing] = useState(false);
  const { toast } = useToast();

  const clearChat = useCallback(async () => {
    if (isClearing) return false;
    
    setIsClearing(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({
          title: "Not Logged In",
          description: "Please log in to clear your chat.",
          variant: "destructive",
        });
        return false;
      }

      const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/chat`;
      
      const resp = await fetch(CHAT_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ clearHistory: true }),
      });

      if (!resp.ok) {
        throw new Error("Failed to clear history");
      }

      // Dispatch event for any listeners (Chat component)
      window.dispatchEvent(new CustomEvent("clearChatHistory"));

      toast({
        title: "Chat Cleared",
        description: "Starting fresh! Ask me anything.",
      });

      onSuccess?.();
      return true;
    } catch (error) {
      if (process.env.NODE_ENV !== "production") {
        console.error("Error clearing chat:", error);
      }
      toast({
        title: "Error",
        description: "Couldn't clear the chat. Try again!",
        variant: "destructive",
      });
      return false;
    } finally {
      setIsClearing(false);
    }
  }, [isClearing, toast, onSuccess]);

  return { clearChat, isClearing };
}
